import type { MenuProps } from '@mui/material/Menu';
import { isValidElement, type ReactNode } from 'react';

import type { IconButtonProps } from '../IconButton/IconButton.model';

/**
 * Simple menu item configuration object
 */
export type SimpleMenuItem = {
  /**
   * Label to display in the menu item
   */
  label: ReactNode;

  /**
   * Unique key for the menu item (optional, will be auto-generated if not provided)
   */
  key?: string;

  /**
   * Click handler for the menu item
   */
  onClick?: () => void;

  /**
   * Optional href for menu item link
   */
  href?: string;

  /**
   * Test ID for the menu item
   */
  testId?: string;

  /**
   * Whether to hide this menu item
   */
  hidden?: boolean;

  /**
   * Whether the menu item is disabled
   */
  disabled?: boolean;

  /**
   * Custom class name for the menu item
   */
  className?: string;
};

/**
 * Menu item can be either a React component or a simple configuration object
 */
export type MenuItemType = ReactNode | SimpleMenuItem;

/**
 * Type guard to check if a menu item is a simple configuration object
 */
export const isSimpleMenuItem = (
  item: MenuItemType
): item is SimpleMenuItem => {
  return (
    typeof item === 'object' &&
    item !== null &&
    !isValidElement(item) &&
    'label' in item
  );
};

export interface IconDropdownProps {
  /**
   * Menu items - can be React components or simple configuration objects
   */
  items: MenuItemType[];

  /**
   * Icon to display in the button (defaults to three horizontal dots)
   */
  icon?: ReactNode;

  /**
   * Props to pass to the IconButton
   */
  iconButtonProps?: Partial<IconButtonProps>;

  /**
   * Props to pass to the Menu component
   */
  menuProps?: Partial<MenuProps>;

  /**
   * Test ID for the trigger button
   */
  testId?: string;

  /**
   * Whether to disable the portal (useful for testing)
   */
  disablePortal?: boolean;

  /**
   * Callback when menu opens
   */
  onOpen?: () => void;

  /**
   * Callback when menu closes
   */
  onClose?: () => void;
}
